import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, Sparkles, Compass, Layers, Brain, Workflow, ShieldCheck,
  Target, Cloud, GitBranch, TrendingUp, Lock, ShoppingBag, Building2, Landmark,
  HeartPulse, Factory, Truck, Hotel, GraduationCap, Film, Check
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScheduleConsultationSection from '../components/ScheduleConsultationSection';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { aiServices, industries, approach, whyChoose, technologies, consultingFaqs } from '../data/aiConsulting';

const iconMap = {
  Compass, Sparkles, Layers, Brain, Workflow, ShieldCheck,
  Target, Cloud, GitBranch, TrendingUp, Lock,
  ShoppingBag, Building2, Landmark, HeartPulse, Factory, Truck, Hotel, GraduationCap, Film
};

const HERO_IMAGE = 'https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxuZXVyYWwlMjBuZXR3b3JrfGVufDB8fHx8MTc4NDU3MzMzOXww&ixlib=rb-4.1.0&q=85&w=1800';
const CIRCUIT_IMAGE = 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHw0fHxuZXVyYWwlMjBuZXR3b3JrfGVufDB8fHx8MTc4NDU3MzMzOXww&ixlib=rb-4.1.0&q=85&w=1200';
const DATA_IMAGE = 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3ODQ3MDY4MjN8MA&ixlib=rb-4.1.0&q=85&w=1400';

const AIConsultingPage = () => {
  return (
    <main className="bg-[#05080f] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="AI network" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05080f]/70 via-[#05080f]/85 to-[#05080f]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.18),transparent_60%)]"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="section-label mb-6">AI Consulting Services</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.03] tracking-tight text-white max-w-4xl">
            Transform ideas into <span className="gradient-text">intelligent business outcomes</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-2xl text-base md:text-lg leading-relaxed">
            Unlock the full potential of Artificial Intelligence with strategic guidance,
            practical implementation, and measurable business impact.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/#services">
              <Button className="btn-gradient rounded-full h-11 px-6 border-0">
                Book an AI Strategy Consultation <ArrowUpRight size={16} className="ml-1" />
              </Button>
            </Link>
            <a href="#services">
              <Button variant="outline" className="rounded-full h-11 px-6 bg-white/5 border-white/10 text-white hover:bg-white/10">
                Explore our services
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* WHY AI CONSULTING MATTERS */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="section-label mb-6">Why AI Consulting Matters</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mb-5">
              Bridge the gap between <span className="gradient-text">business objectives</span> and technology
            </h2>
            <p className="text-slate-400 leading-relaxed">
              Many organizations invest in AI without a clear strategy, resulting in expensive experiments that fail to deliver business outcomes.
            </p>
            <p className="text-slate-400 leading-relaxed mt-4">
              We help you identify high-impact opportunities, prioritize initiatives, select the right technologies,
              and deploy AI responsibly — focused on measurable business value, not just impressive demonstrations.
            </p>
          </div>
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10">
            <img src={CIRCUIT_IMAGE} alt="AI circuit" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#05080f]/70 via-transparent to-amber-500/10"></div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section id="services" className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Our AI Consulting Services</div>
          <div className="grid md:grid-cols-2 gap-6 items-end mb-14">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight">
              Six specialized practices, <br className="hidden md:block" />
              one intelligent outcome
            </h2>
            <p className="text-slate-400 max-w-md md:mt-4">
              Mix-and-match services aligned to your maturity — from strategy sprints to full transformation programs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiServices.map((svc) => {
              const Icon = iconMap[svc.icon] || Sparkles;
              return (
                <div key={svc.id} className="group card-dark rounded-3xl p-7 relative overflow-hidden hover:border-amber-500/30 transition-colors">
                  <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full blur-3xl opacity-0 group-hover:opacity-30 bg-amber-500 transition-opacity duration-500"></div>

                  <div className="relative flex items-start justify-between mb-5">
                    <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center">
                      <Icon className="text-amber-400" size={22} strokeWidth={1.5} />
                    </div>
                  </div>

                  <h3 className="relative font-display text-white text-xl font-semibold mb-2">{svc.title}</h3>
                  <p className="relative text-amber-300 text-sm mb-4">{svc.tagline}</p>

                  <ul className="relative space-y-2">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check size={14} className="text-amber-400 mt-1 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Our AI Consulting Approach</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight">
              A proven, <span className="gradient-text">outcomes-driven</span> journey
            </h2>
            <p className="text-slate-400 max-w-md">
              Five deliberate stages that de-risk your AI investment and accelerate time-to-value.
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
            <div className="grid md:grid-cols-5 gap-6">
              {approach.map((p) => (
                <div key={p.step} className="relative card-dark rounded-2xl p-6 hover:border-amber-500/30 transition-colors">
                  <div className="relative z-10 h-11 w-11 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 font-display font-semibold flex items-center justify-center mb-4">
                    {p.step}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{p.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={DATA_IMAGE} alt="Data visualization" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05080f] via-[#05080f]/85 to-[#05080f]"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Industries We Serve</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
            AI expertise across <span className="gradient-text">every sector</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mb-12">
            Deep domain understanding lets us translate AI capabilities into real, sector-specific value.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((i) => {
              const Icon = iconMap[i.icon] || Sparkles;
              return (
                <div key={i.name} className="card-dark rounded-2xl p-5 flex flex-col items-center text-center hover:border-amber-500/30 transition-colors group">
                  <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-3 group-hover:bg-amber-500/15">
                    <Icon className="text-amber-400" size={20} strokeWidth={1.5} />
                  </div>
                  <span className="text-slate-200 text-sm font-medium">{i.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Why Choose Us</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-14">
            Practical AI. <br className="md:hidden" />
            <span className="gradient-text">Measurable impact.</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {whyChoose.map((w) => {
              const Icon = iconMap[w.icon] || Sparkles;
              return (
                <div key={w.title} className="card-dark rounded-3xl p-7 hover:border-amber-500/30 transition-colors">
                  <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-5">
                    <Icon className="text-amber-400" size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-white text-lg font-semibold mb-2">{w.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{w.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TECHNOLOGIES */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Technologies We Work With</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mb-10">
            Best-in-class platforms, thoughtfully composed
          </h2>

          <div className="flex flex-wrap gap-2.5">
            {technologies.map((t) => (
              <span key={t} className="text-sm px-4 py-2 rounded-full bg-amber-500/8 text-amber-100 border border-amber-500/25 hover:bg-amber-500/15 transition-colors">
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">FAQ</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-10">
            Common questions
          </h2>

          <Accordion type="single" collapsible className="space-y-3">
            {consultingFaqs.map((f) => (
              <AccordionItem key={f.q} value={f.q} className="card-dark rounded-2xl px-6 border !border-white/10">
                <AccordionTrigger className="text-left text-white hover:no-underline hover:text-amber-300 py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Schedule consultation */}
      <ScheduleConsultationSection
        label="Schedule a Call"
        title={<>Book your free <span className="gradient-text">AI strategy session</span></>}
        subtitle="Pick any weekday slot to speak with our AI consultants about your goals, roadmap, and next-best AI moves."
      />

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="card-dark rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0">
              <img src={CIRCUIT_IMAGE} alt="" className="w-full h-full object-cover opacity-15" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.18),transparent_60%)]"></div>
            </div>
            <div className="relative">
              <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
                Ready to build your <span className="gradient-text">AI strategy?</span>
              </h2>
              <p className="text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                Whether you’re exploring Generative AI, intelligent automation, machine learning, or enterprise AI
                transformation — our experts are ready to help you turn ideas into measurable business outcomes.
              </p>
              <Link to="/#contact">
                <Button className="btn-gradient rounded-full h-12 px-8 border-0 text-base">
                  Schedule a free AI consultation
                </Button>
              </Link>
              <p className="text-slate-500 text-xs mt-4">Let’s build the future of your business with AI.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default AIConsultingPage;
